import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { Plus } from '@creditornot/cb-icons';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import { ValidationErrors } from 'final-form';
import React, { useState } from 'react';
import { Form, useField } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { WoltModal } from 'common/components';
import { CampaignDiscountedItemFormValues } from 'modules/campaigns/types';

import EndOfDayItemsCountField from './EndOfDayItemsCountField';
import { DiscountedItem } from '../../../hooks/useDiscountedItems';
import CampaignFormItem from '../../CampaignFormItem';
import UnsavedChangesModal from '../../modals/UnsavedChangesModal/UnsavedChangesModal';
import { DiscountedItemCountSelector } from '../DiscountedItemCountSelector/DiscountedItemCountSelector';
import { DiscountedItemDetails } from '../DiscountedItemDetails/DiscountedItemDetails';
import { DiscountedItemSelectorModal } from '../DiscountedItemSelectorModal/DiscountedItemSelectorModal';

type DiscountedItemFormItemProps = {
  initialValues: CampaignDiscountedItemFormValues;
  discountedItems: DiscountedItem[];
  errors?: ValidationErrors;
  disabled: boolean;
  isLoadingMenuData: boolean;
  menuId: string;
  onUpdate: (values: CampaignDiscountedItemFormValues) => void;
  'data-testid'?: string;
  isEndOfDayCampaign?: boolean;
};

const StyledItemContainer = styled.div<{ stretch: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: ${({ stretch }) => (stretch ? 'stretch' : 'flex-start')};
  margin-block-start: 4px;
`;

const Divider = styled.div`
  border-block-start: 1px solid ${cssThemedValue('borderPrimary')};
`;

const DEFAULT_ITEM_TRIGGER_COUNT = 1;

export const DiscountedItemFormItem = ({
  initialValues,
  discountedItems,
  errors,
  disabled,
  isLoadingMenuData,
  menuId,
  isEndOfDayCampaign,
  onUpdate,
  ...props
}: DiscountedItemFormItemProps) => {
  const [isDiscountedItemSelectorModalOpen, setIsDiscountedItemSelectorModalOpen] = useState(false);
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState<boolean>(false);
  const freeItemsField = useField('freeItemsCount');
  const handleCloseModals = (isFormDirty?: boolean) => {
    if (isFormDirty) {
      setIsUnsavedChangesModalOpen(true);
    } else {
      setIsUnsavedChangesModalOpen(false);
      setIsDiscountedItemSelectorModalOpen(false);
    }
  };

  const handleDiscountedItemUpdate = (values: CampaignDiscountedItemFormValues) => {
    onUpdate(values);
    handleCloseModals();
  };

  const { formatMessage } = useIntl();
  return (
    <>
      <Form<CampaignDiscountedItemFormValues> onSubmit={handleDiscountedItemUpdate} initialValues={initialValues}>
        {({ handleSubmit, dirty, form }) => (
          <WoltModal isOpen={isDiscountedItemSelectorModalOpen} onRequestClose={() => handleCloseModals(dirty)}>
            <DiscountedItemSelectorModal
              discountedItems={discountedItems}
              onClose={() => handleCloseModals(dirty)}
              onUpdate={handleSubmit}
              isLoadingMenuData={isLoadingMenuData}
            />
            <UnsavedChangesModal
              isOpen={dirty && isUnsavedChangesModalOpen}
              onDropChanges={() => {
                handleCloseModals();
                form.reset();
              }}
              onClose={() => setIsUnsavedChangesModalOpen(false)}
            />
          </WoltModal>
        )}
      </Form>
      <CampaignFormItem
        data-testid={props['data-testid']}
        title={<FormattedMessage id="campaigns.form.item.what-item-has" />}
        value={
          <StyledItemContainer stretch={!!initialValues.itemId}>
            {initialValues.itemId ? (
              <>
                <DiscountedItemDetails
                  itemId={initialValues.itemId}
                  menuId={menuId}
                  onRemove={
                    disabled
                      ? undefined
                      : () => onUpdate({ itemId: undefined, itemPrice: undefined, itemTriggerCount: undefined })
                  }
                />
                <Divider />
                {isEndOfDayCampaign ? (
                  <EndOfDayItemsCountField />
                ) : (
                  <DiscountedItemCountSelector
                    value={initialValues.itemTriggerCount || DEFAULT_ITEM_TRIGGER_COUNT}
                    disabled={disabled}
                    onChange={() => {
                      freeItemsField.input.onChange(1);
                    }}
                  />
                )}
              </>
            ) : (
              <Button
                variant="blue"
                size="large"
                icon={<Plus />}
                iconPosition="right"
                onClick={() => setIsDiscountedItemSelectorModalOpen(true)}
              >
                <FormattedMessage id="campaigns.form.item.add-item" />
              </Button>
            )}
            {!!errors?.itemId && <Alert variant="error">{formatMessage({ id: errors?.itemId.id })}</Alert>}
          </StyledItemContainer>
        }
      ></CampaignFormItem>
    </>
  );
};
